import {
	Box,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getConfirmedOrders, getConfirmedOrdersLoader } from 'redux/reducers/orders/selectors';
import { useParams } from 'react-router';
import { getConfirmedOrdersRequest } from 'redux/reducers/orders/reducer';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { nanoid } from '@reduxjs/toolkit';
import { representationSchemeType } from 'services/constants/representationSchemeType';
import { SCHEMES_TO_VAT_CORRECTIONS_SCHEMES } from 'redux/reducers/vatCorrections/constants';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { TOrdersTableProps } from './types';

export const OrdersTable: FC<TOrdersTableProps> = ({ externalId, setExternalId, setEasId }) => {
	const params = useParams<{ orderId?: string }>();
	const orderIdParam = params.orderId ?? '';
	const dispatch = useDispatch();
	const confirmedOrder = useSelector(getConfirmedOrders);
	// const [id, setId] = useState<string | null>(null);

	const handleSelectRow = (id: string, easId: number) => {
		setExternalId(id);
		setEasId(easId);

		// setOrderId(i);
	};

	useEffect(
		() => {
			if (confirmedOrder?.data.length === 1) {
				const schemes = confirmedOrder.data[0]?.schemes?.split(',');
				const noOneSchemes = !schemes?.some(
					(scheme) =>
						SCHEMES_TO_VAT_CORRECTIONS_SCHEMES?.[
							scheme as keyof typeof SCHEMES_TO_VAT_CORRECTIONS_SCHEMES
						],
				);
				if (!noOneSchemes) {
					handleSelectRow(confirmedOrder?.data[0]?.externalOrderId, confirmedOrder?.data[0]?.id);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[confirmedOrder?.data],
	);

	return (
		<TableContainer
			sx={{
				overflowY: 'hidden',
				border: '1px solid #e0e0e0',
				borderRadius: '12px',
				mt: 2,
			}}
		>
			<Table>
				<TableHead
					sx={{
						bgcolor: 'primary.light',
					}}
				>
					<TableRow>
						<TableCell
							sx={{
								m: 0,
								p: 0.5,
							}}
						>
							Store Order #
						</TableCell>
						<TableCell
							sx={{
								m: 0,
								p: 0.5,
							}}
						>
							EAS Order Id
						</TableCell>
						<TableCell
							sx={{
								m: 0,
								p: 0.5,
							}}
						>
							Delivery Country
						</TableCell>
						<TableCell
							sx={{
								m: 0,
								p: 0.5,
							}}
						>
							Schemes
						</TableCell>
						<TableCell
							align="right"
							sx={{
								m: 0,
								p: 0.5,
							}}
						>
							Total Order Value
						</TableCell>
						<TableCell
							align="right"
							sx={{
								m: 0,
								p: 0.5,
							}}
						>
							Total VAT
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{confirmedOrder?.data.map((order) => {
						const schemes = order?.schemes?.split(',');
						const noOneSchemes = !schemes?.some(
							(scheme) =>
								SCHEMES_TO_VAT_CORRECTIONS_SCHEMES?.[
									scheme as keyof typeof SCHEMES_TO_VAT_CORRECTIONS_SCHEMES
								],
						);
						return (
							<TableRow
								key={order.id}
								sx={{
									m: 0,
									p: 0,
									'&:hover': {
										bgcolor: noOneSchemes
											? 'none'
											: externalId === order.externalOrderId
												? '#bdd3dfa5'
												: '#f7f7f7',
									},
									cursor: noOneSchemes ? 'not-allowed' : 'pointer',
									bgcolor: externalId === order.externalOrderId ? '#bdd3df85' : 'transparent',
									opacity: noOneSchemes ? 0.5 : 1,
								}}
								onClick={() => {
									if (!noOneSchemes) {
										handleSelectRow(order.externalOrderId, order.id);
									}
								}}
							>
								<TableCell
									sx={{
										m: 0,
										p: 1,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										maxWidth: '200px',
									}}
								>
									{order.externalOrderId}
								</TableCell>

								<TableCell
									sx={{
										m: 0,
										p: 1,
									}}
								>
									{order.id}
								</TableCell>
								<TableCell
									sx={{
										m: 0,
										p: 1,
									}}
								>
									<CountryWithFlag countryCode={order.deliveryCountry} size="small" />
								</TableCell>
								<TableCell
									sx={{
										m: 0,
										p: 1,
									}}
								>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'start',
											width: '100%',
											gap: '5px',
										}}
									>
										{schemes?.map((scheme) => {
											return (
												<Chip
													key={nanoid()}
													label={
														representationSchemeType[
															scheme as keyof typeof representationSchemeType
														]
													}
													// color={
													// 	order?.recipient_company_vat_validated === 'validated' ? 'success' : 'warning'
													// }
													size="small"
													sx={{
														p: '0 5px',
														fontSize: '.8rem',
														height: '20px',
														// fontWeight: '550',
														// border: '1px solid #ffffff',
														bgcolor: SCHEMES_TO_VAT_CORRECTIONS_SCHEMES?.[
															scheme as keyof typeof SCHEMES_TO_VAT_CORRECTIONS_SCHEMES
														]
															? 'primary.light'
															: '#00000040',
														color: '#00000090',
													}}
												/>
											);
										})}
									</Box>
								</TableCell>
								<TableCell
									align="right"
									sx={{
										m: 0,
										p: 1,
									}}
								>
									<AmountAndCurrency
										amount={order.totalOrderValue}
										currency={order.paymentCurrency}
									/>
								</TableCell>
								<TableCell
									align="right"
									sx={{
										m: 0,
										p: 1,
									}}
								>
									<AmountAndCurrency amount={order.totalVat} currency={order.paymentCurrency} />
								</TableCell>
							</TableRow>
						);
					})}
					{(confirmedOrder?.totalCount ?? 0) > 6 && (
						<TableRow>
							<TableCell
								sx={{
									height: '10px',
									m: '0',
									p: '0 20px',
									color: 'primary.main',
									fontSize: 0,
									borderBottom: 'none',
								}}
							>
								<MoreHorizIcon
									fontSize="large"
									sx={{
										p: 0,
										m: '-5px',
									}}
								/>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
